import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  useTheme,
  Paper,
  Stack,
  Divider,
} from '@mui/material';
import { motion } from 'framer-motion';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import PublicIcon from '@mui/icons-material/Public';
import HandshakeIcon from '@mui/icons-material/Handshake';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';

const MotionBox = motion(Box);

const aboutImages = [
  'https://images.pexels.com/photos/3785935/pexels-photo-3785935.jpeg?auto=compress&cs=tinysrgb&w=400',
  'https://images.pexels.com/photos/4489728/pexels-photo-4489728.jpeg?auto=compress&cs=tinysrgb&w=400',
  'https://images.pexels.com/photos/162553/keys-workshop-mechanic-tools-162553.jpeg?auto=compress&cs=tinysrgb&w=400',
];

const FeatureItem = ({ icon, title, description, isDark }) => (
  <MotionBox
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
    sx={{ display: 'flex', gap: 2, mb: 3 }}
  >
    <Box 
      sx={{ 
        color: '#e57373',
        bgcolor: isDark ? 'rgba(229, 115, 115, 0.1)' : 'rgba(229, 115, 115, 0.05)',
        p: 1.5,
        borderRadius: 2,
        height: 'fit-content',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {icon}
    </Box>
    <Box>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
        {description}
      </Typography>
    </Box>
  </MotionBox>
);

const About = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Box
      id="about"
      sx={{
        py: { xs: 8, md: 12 },
        bgcolor: isDark ? 'background.default' : '#f8f9fa',
        overflow: 'hidden',
      }}
    >
      <Container maxWidth="xl">
        <Grid container spacing={8} alignItems="center">
          {/* Left Content */}
          <Grid item xs={12} md={7}>
            <MotionBox
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                <Divider sx={{ width: 40, borderColor: '#e57373', borderWidth: 2 }} />
                <Typography
                  variant="subtitle1"
                  sx={{ 
                    color: '#e57373',
                    fontWeight: 700, 
                    letterSpacing: 2,
                    textTransform: 'uppercase'
                  }}
                >
                  SINCE 2012
                </Typography>
              </Stack>
              
              <Typography
                variant="h2"
                sx={{
                  fontFamily: '"Orbitron", "Rajdhani", sans-serif',
                  fontWeight: 700,
                  color: 'text.primary',
                  mb: 2,
                  fontSize: { xs: '2rem', md: '3rem' },
                  lineHeight: 1.2,
                }}
              >
                MOHAMMED SAEED AL AMOUDI
                <Box component="span" sx={{ color: '#e57373', display: 'block', mt: 1 }}>
                  TRADING ESTABLISHMENT
                </Box>
              </Typography>
              
              <Typography
                variant="h6"
                sx={{
                  color: 'text.secondary',
                  mb: 5,
                  fontWeight: 400,
                  maxWidth: '90%',
                  fontStyle: 'italic',
                }}
              >
                "Trusted service provider committed to excellence in industrial solutions."
              </Typography>

              <Grid container spacing={4} sx={{ mb: 4 }}>
                <Grid item xs={12} md={6}>
                  <FeatureItem 
                    isDark={isDark}
                    icon={<WorkspacePremiumIcon />}
                    title="Quality Commitment"
                    description="We guarantee high-quality and durable bearings, spare parts, and tools that meet the highest international standards and technical specifications."
                  />
                  <FeatureItem 
                    isDark={isDark}
                    icon={<VerifiedUserIcon />}
                    title="After-Sales Guarantee"
                    description="We stand by our products with a one-month replacement guarantee for any manufacturing defects or malfunctions not caused by misuse."
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FeatureItem 
                    isDark={isDark}
                    icon={<PublicIcon />}
                    title="Global Sourcing"
                    description="Our premium production lines utilize components sourced directly from Europe, Japan, and trusted global partners."
                  />
                  <FeatureItem 
                    isDark={isDark}
                    icon={<HandshakeIcon />}
                    title="Expert Partnership"
                    description="With deep industry relationships and experienced distribution agents, we ensure reliable supply and technical support for your operations."
                  />
                </Grid>
              </Grid>

              <Typography variant="body1" paragraph sx={{ mb: 4, color: 'text.secondary' }}>
                If you are interested in purchasing high-quality bearings and spare parts for your operations, kindly feel free to contact us. Our sales team will be pleased to assist you.
              </Typography>

              <Button
                variant="contained"
                size="large"
                sx={{
                  bgcolor: '#e57373',
                  color: 'white',
                  px: 5,
                  py: 1.5,
                  borderRadius: 50,
                  boxShadow: '0 8px 20px rgba(229, 115, 115, 0.4)',
                  fontWeight: 600,
                  letterSpacing: 1,
                  '&:hover': {
                    bgcolor: '#ef5350',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 12px 25px rgba(229, 115, 115, 0.5)',
                  },
                  transition: 'all 0.3s ease',
                }}
                onClick={() => {
                  const element = document.querySelector('#contact');
                  if (element) element.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                Contact Us Today
              </Button>
            </MotionBox>
          </Grid>

          {/* Right Images - Masonry Style */}
          <Grid item xs={12} md={5}>
            <Box sx={{ position: 'relative', height: { xs: 400, md: 600 } }}>
               <MotionBox
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                sx={{
                  position: 'absolute',
                  top: '10%',
                  right: '10%',
                  width: '70%',
                  height: '60%',
                  borderRadius: 4,
                  overflow: 'hidden',
                  boxShadow: isDark ? '0 20px 40px rgba(0,0,0,0.5)' : '0 20px 40px rgba(0,0,0,0.2)',
                  zIndex: 2,
                }}
              >
                <Box
                  component="img"
                  src={aboutImages[0]}
                  alt="Industrial Workshop"
                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </MotionBox>
              
              <MotionBox
                initial={{ opacity: 0, scale: 0.8, x: -20, y: 20 }}
                whileInView={{ opacity: 1, scale: 1, x: 0, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
                sx={{
                  position: 'absolute',
                  bottom: '5%',
                  left: '5%',
                  width: '50%',
                  height: '45%',
                  borderRadius: 4,
                  overflow: 'hidden',
                  boxShadow: isDark ? '0 20px 40px rgba(0,0,0,0.5)' : '0 20px 40px rgba(0,0,0,0.2)',
                  zIndex: 3,
                  border: `4px solid ${isDark ? '#303030' : '#fff'}`,
                }}
              >
                <Box
                  component="img"
                  src={aboutImages[1]}
                  alt="Mechanic Tools"
                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </MotionBox>

              {/* Decorative circle */}
              <Box
                sx={{
                  position: 'absolute',
                  top: '5%',
                  right: '5%',
                  width: 200,
                  height: 200,
                  borderRadius: '50%',
                  border: '2px solid rgba(229, 115, 115, 0.3)',
                  zIndex: 1,
                }}
              />
               <Box
                sx={{
                  position: 'absolute',
                  bottom: '15%',
                  left: '15%',
                  width: 150,
                  height: 150,
                  borderRadius: '50%',
                  bgcolor: 'rgba(229, 115, 115, 0.05)',
                  zIndex: 1,
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default About;
